import { LitElement, html } from 'lit';

import { api } from '../api/index.js';
import '../components/customer-members.js';
import { stateBadge } from '../components/plane-badge.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { poll } from '../poll.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { customerPageStyles } from '../../styles/pages/customer-page.js';

/**
 * Operator view of one customer: its instances and its members. A member of a customer sees that
 * customer's instances, and only those; an instance is given to a customer from its own page, or
 * when it is created. People join through an invitation link, which creates their account; an
 * existing Dash user can also be added here by name.
 */
class CustomerPage extends LitElement {
  static properties = {
    customerId: { type: String },
    busy: { state: true },
    actionError: { state: true },
  };

  static styles = [
    pageStyles,
    tableStyles,
    formStyles,
    customerPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.busy = false;
    this.actionError = null;
    this.poll = poll(this, async () => {
      const [customers, instances] = await Promise.all([api.customers(), api.instances()]);
      const customer = customers.find((candidate) => candidate.id === this.customerId);
      if (!customer) {
        throw Object.assign(new Error(`customer ${this.customerId} not found`), { status: 404 });
      }
      return { customer, instances: instances.filter((instance) => customer.instances.includes(instance.name)) };
    });
  }

  updated(changed) {
    if (changed.has('customerId') && changed.get('customerId') !== undefined) {
      this.poll.reset();
    }
  }

  async addUser(event, customer) {
    event.preventDefault();
    const input = event.target.elements.username;
    this.busy = true;
    this.actionError = null;
    try {
      await api.addCustomerUser(customer.id, input.value.trim());
      input.value = '';
      this.renderRoot.querySelector('plane-customer-members')?.poll.refresh();
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  renderInstances(customer, instances) {
    const create = html`<a class="button small" href="/workspaces/new?customer=${encodeURIComponent(customer.id)}">New workspace</a>`;
    if (instances.length === 0) {
      return html`
        <h2>Workspaces ${create}</h2>
        <p class="empty">None yet: create one for this customer, or assign an existing one from its page.</p>
      `;
    }
    return html`
      <h2>Workspaces ${create}</h2>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Workspace</th>
              <th>State</th>
              <th>Server</th>
              <th>Image</th>
            </tr>
          </thead>
          <tbody>
            ${instances.map(
              (instance) => html`
                <tr>
                  <td><a href="/workspaces/${instance.ref ?? instance.name}">${instance.displayName}</a><div class="muted"><code>${instance.name}</code></div></td>
                  <td>${stateBadge(instance.state)}</td>
                  <td><a href="/servers/${instance.serverId}"><code>${instance.serverId}</code></a></td>
                  <td><code>${instance.imageVersion}</code></td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/customers">Customers</a> / ${this.poll.value?.customer.name ?? this.customerId}</nav>`;
    const pending = loadState(this.poll, 'the customer');
    if (pending) {
      return html`${crumbs}${pending}`;
    }
    const { customer, instances } = this.poll.value;
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>${customer.name}</h1>
          <p class="lead">Its members see these workspaces and nothing else.</p>
        </div>
      </header>
      ${staleWarning(this.poll)}
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The action failed</strong>${this.actionError}</div>` : null}
      <section>${this.renderInstances(customer, instances)}</section>
      <section>
        <h2>People</h2>
        <div class="card">
          <plane-customer-members
            .customerId=${customer.id}
            .customerName=${customer.name}
            .canManage=${true}
            @members-changed=${() => this.poll.refresh()}
          ></plane-customer-members>
          <form class="inline" @submit=${(event) => this.addUser(event, customer)}>
            <input name="username" type="text" required placeholder="Existing Dash user name" aria-label="Existing user to add to ${customer.name}" />
            <button class="small" ?disabled=${this.busy}>Add user</button>
          </form>
        </div>
      </section>
    `;
  }
}

customElements.define('plane-customer-page', CustomerPage);
